import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Plus, Trash2, ShoppingCart, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { usePsStore } from "../../store/psStore";
import { psSetPharmacyMandate } from "../../lib/psApi";
import { metaFor } from "../../components/ServiceMeta";

const PREFS = [
  { k: "ASK_ME", label: "Me demander" },
  { k: "AUTO_REPLACE", label: "Remplacer" },
  { k: "NO_REPLACEMENT", label: "Ne rien mettre" },
];
const PREF_LABELS = { ASK_ME: "🔔 Me demander", AUTO_REPLACE: "🔄 Remplacer", NO_REPLACEMENT: "🚫 Ne rien mettre" };

export default function PsServiceDetail() {
  const { stop } = useParams();
  const stopType = String(stop || "").toUpperCase();
  const nav = useNavigate();
  const { order, addItem, deleteItem, refresh } = usePsStore();
  const m = metaFor(stopType);
  const Icon = m.Icon;

  const [name, setName] = useState("");
  const [qty, setQty] = useState("1");
  const [note, setNote] = useState("");
  const [pref, setPref] = useState("ASK_ME");
  const [saving, setSaving] = useState(false);
  const [mandate, setMandate] = useState(false);
  const [instruction, setInstruction] = useState("");
  const [savingMandate, setSavingMandate] = useState(false);

  useEffect(() => { refresh().catch(() => {}); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const sub = order?.sub_orders?.find((s) => s.stop_type === stopType);
  const items = sub?.items || [];
  const isPharmacy = sub?.fulfillment_type === "MERCHANT_PREPARED";

  const onAdd = async () => {
    if (saving) return;
    if (!name.trim()) { toast.error("Indiquez le nom de l'article"); return; }
    setSaving(true);
    try {
      await addItem(stopType, { product_name: name.trim(), quantity: parseFloat(qty) || 1, note: note.trim() || undefined, substitution_preference: isPharmacy ? undefined : pref });
      setName(""); setQty("1"); setNote("");
    } catch { toast.error("Impossible d'ajouter cet article"); }
    finally { setSaving(false); }
  };

  const onSaveMandate = async () => {
    if (savingMandate || !order) return;
    if (!mandate) { toast.error("Acceptez le mandat de retrait par La BASE"); return; }
    setSavingMandate(true);
    try {
      await psSetPharmacyMandate(order.id, { accepted: true, instruction: instruction.trim() || undefined });
      toast.success("Mandat enregistré — vos documents sont transmis de manière sécurisée.");
    } catch { toast.error("Impossible d'enregistrer le mandat"); }
    finally { setSavingMandate(false); }
  };

  return (
    <div className="max-w-2xl mx-auto px-4 py-6">
      <div className="flex items-center gap-3 mb-5">
        <button onClick={() => nav("/personal-shopper")} data-testid="ps-detail-back" style={{ color: "#0b1220" }}><ArrowLeft size={24} /></button>
        <Icon size={22} color={m.accent} />
        <h1 className="text-xl font-extrabold" style={{ color: "#0b1220" }}>{m.label}</h1>
      </div>

      <p className="text-sm mb-5" style={{ color: "#64748b" }}>
        {isPharmacy ? "Décrivez votre demande (ordonnance, produit). Le marchand prépare votre retrait."
          : "Ajoutez les articles à acheter pour vous. Votre shopper fait les courses en rayon."}
      </p>

      {isPharmacy && (
        <div className="rounded-2xl p-4 mb-5" style={{ background: "#f0f9ff", border: "1px solid #bae6fd" }}>
          <p className="font-extrabold mb-3" style={{ color: "#0b1220" }}>Mandat de retrait pharmacie</p>
          <label className="flex items-start gap-3 mb-3 cursor-pointer">
            <input type="checkbox" checked={mandate} onChange={(e) => setMandate(e.target.checked)} data-testid="ps-pharma-mandate" className="mt-1" />
            <span className="text-sm" style={{ color: "#334155" }}>J'autorise La BASE à retirer ma commande en pharmacie en mon nom.</span>
          </label>
          <input value={instruction} onChange={(e) => setInstruction(e.target.value)} placeholder="Instruction éventuelle (facultatif)" data-testid="ps-pharma-instruction"
            className="w-full rounded-lg px-3 py-2.5 text-sm outline-none mb-3" style={{ background: "#fff", border: "1px solid #bae6fd" }} />
          <button onClick={onSaveMandate} disabled={savingMandate} data-testid="ps-pharma-save"
            className="w-full rounded-xl font-bold text-white py-3 flex items-center justify-center gap-2" style={{ background: m.accent }}>
            <ShieldCheck size={18} /> {savingMandate ? "Enregistrement…" : "Valider le mandat"}
          </button>
        </div>
      )}

      <div className="bg-white rounded-2xl p-4 mb-6" style={{ border: "1px solid #f0f0f0" }}>
        <input value={name} onChange={(e) => setName(e.target.value)} placeholder={isPharmacy ? "Ex. Doliprane 1000 mg" : "Ex. Lait entier 1L"} data-testid="ps-item-name"
          className="w-full rounded-lg px-3.5 py-3 text-[15px] outline-none mb-2.5" style={{ background: "#f1f5f9", color: "#0b1220" }} />
        <div className="flex gap-2.5 mb-2.5">
          <input value={qty} onChange={(e) => setQty(e.target.value)} placeholder="Qté" inputMode="numeric" data-testid="ps-item-qty"
            className="w-24 rounded-lg px-3.5 py-3 text-[15px] outline-none" style={{ background: "#f1f5f9", color: "#0b1220" }} />
          <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Note (facultatif)" data-testid="ps-item-note"
            className="flex-1 rounded-lg px-3.5 py-3 text-[15px] outline-none" style={{ background: "#f1f5f9", color: "#0b1220" }} />
        </div>
        {!isPharmacy && (
          <div className="mb-3">
            <p className="text-xs font-semibold mb-1.5" style={{ color: "#64748b" }}>Si le produit est indisponible :</p>
            <div className="flex gap-2">
              {PREFS.map((o) => (
                <button key={o.k} onClick={() => setPref(o.k)} data-testid={`ps-pref-${o.k}`}
                  className="flex-1 rounded-lg py-2 text-xs font-bold"
                  style={{ border: `1px solid ${pref === o.k ? m.accent : "#e2e8f0"}`, background: pref === o.k ? m.accent : "#fff", color: pref === o.k ? "#fff" : "#475569" }}>
                  {o.label}
                </button>
              ))}
            </div>
          </div>
        )}
        <button onClick={onAdd} disabled={saving} data-testid="ps-item-add"
          className="w-full rounded-xl font-bold text-white py-3 flex items-center justify-center gap-2" style={{ background: m.accent }}>
          <Plus size={20} /> {saving ? "Ajout…" : "Ajouter à ma liste"}
        </button>
      </div>

      <p className="font-extrabold mb-3" style={{ color: "#0b1220" }}>Ma liste {m.label} ({items.length})</p>
      {items.length === 0 ? (
        <p className="text-sm" style={{ color: "#94a3b8" }}>Aucun article pour l'instant.</p>
      ) : items.map((it) => (
        <div key={it.id} className="flex items-center gap-3 bg-white rounded-xl p-3.5 mb-2" style={{ border: "1px solid #f0f0f0" }} data-testid={`ps-item-${it.id}`}>
          <div className="flex-1">
            <p className="font-bold text-sm" style={{ color: "#0b1220" }}>{it.product_name}</p>
            <p className="text-xs mt-0.5" style={{ color: "#64748b" }}>Qté {it.quantity}{it.note ? ` · ${it.note}` : ""}{it.substitution_preference ? ` · ${PREF_LABELS[it.substitution_preference] || ""}` : ""}</p>
          </div>
          <button onClick={() => deleteItem(stopType, it.id)} data-testid={`ps-item-del-${it.id}`} className="p-1.5" style={{ color: "#dc2626" }}><Trash2 size={18} /></button>
        </div>
      ))}

      <button onClick={() => nav("/personal-shopper/panier")} data-testid="ps-detail-goto-cart"
        className="w-full rounded-2xl font-bold text-white py-4 mt-6 flex items-center justify-center gap-2" style={{ background: "#0b1220" }}>
        <ShoppingCart size={20} /> Voir mon panier
      </button>
    </div>
  );
}
